import axios from 'axios';
import requestManager, { withRequestManagement } from '../utils/requestManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
});

// Global request throttling for all API calls
let globalRequestThrottle = {
  lastUpdate: 0,
  cooldownPeriod: 15000, // 15 seconds minimum between location updates
  pendingRequests: new Set(),
  maxConcurrentRequests: 1, // Only 1 concurrent location update
  failureCount: 0,
  lastFailure: 0,
  backoffMultiplier: 1,
  circuitBreakerOpen: false,
  circuitBreakerTimeout: 0
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_NETWORK' || error.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
      console.warn('Network error detected, implementing circuit breaker');
      // Implement circuit breaker pattern
      globalRequestThrottle.failureCount++;
      globalRequestThrottle.lastFailure = Date.now();
      
      // Open circuit breaker after 3 consecutive failures
      if (globalRequestThrottle.failureCount >= 3) {
        globalRequestThrottle.circuitBreakerOpen = true;
        globalRequestThrottle.circuitBreakerTimeout = Date.now() + 60000; // 1 minute timeout
        console.warn('Circuit breaker opened due to repeated failures');
      }
    }
    return Promise.reject(error);
  }
);

export const riderService = {
  // Get rider profile
  async getProfile() {
    const response = await api.get('/rider/profile');
    return response.data;
  },

  // Update rider profile
  async updateProfile(profileData) {
    const response = await api.put('/rider/profile', profileData);
    return response.data;
  },

  // Update rider location with global request management
  updateLocation: withRequestManagement(async (locationData) => {
    const response = await api.put('/rider/location', locationData, {
      timeout: 20000 // 20 second timeout
    });
    return response.data;
  }, 'rider-location'),

  // Update availability status
  async updateAvailability(isAvailable) {
    const response = await api.put('/rider/availability', { isAvailable });
    return response.data;
  },

  // Get rider's assigned orders
  async getMyOrders() {
    const response = await api.get('/rider/orders');
    return response.data;
  },

  // Verification methods
  async getVerificationStatus() {
    const response = await api.get('/rider/verification');
    return response.data;
  },

  async uploadVerificationDocument(documentType, file) {
    console.log('Uploading document:', { documentType, file });
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }
    
    const response = await api.post('/rider/verification/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log('Upload response:', response.data);
    return response.data;
  },

  async submitForVerification() {
    console.log('Submitting rider verification...');
    try {
      const response = await api.post('/rider/verification/submit');
      console.log('Rider verification submitted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting rider verification:', error);
      throw error;
    }
  }
};

export default riderService;
